/**
 * Number and time formatting utilities.
 */
import { toSeconds } from "@speakai/common/src/utils/transcript-time";
import { formatClock } from "@speakai/common/src/utils/clock";
import { formatDurationWords } from "@speakai/common/src/utils/format";

const FILE_SIZE_UNITS = ["bytes", "KB", "MB", "GB", "TB", "PB"];

/**
 * Format a byte count into a human-readable file size string.
 *
 * @param bytes - Number of bytes
 * @param precision - Decimal places (default 2)
 * @returns Formatted string like "1.50 MB"
 */
export function formatFileSize(
  bytes: number = 0,
  precision: number = 2,
): string {
  if (isNaN(parseFloat(String(bytes))) || !isFinite(bytes)) {
    return "?";
  }

  let unit = 0;
  let size = bytes;

  while (size >= 1024 && unit < FILE_SIZE_UNITS.length - 1) {
    size /= 1024;
    unit++;
  }

  return size.toFixed(precision) + " " + FILE_SIZE_UNITS[unit];
}

/**
 * Format a duration in seconds to HH:MM:SS or MM:SS.
 * Omits hours if zero.
 *
 * @param duration - Duration in seconds (may include decimals)
 * @returns Formatted string like "1:03:45" or "03:45"
 */
/**
 * Format seconds as HH:MM:SS.mmm for the transcript timestamp editor.
 *
 * Separate from formatDuration, which rounds for display and is used across
 * listings: segment boundaries are edited against the source transcription and
 * need every millisecond visible, not a friendly approximation.
 */
export function formatTimecode(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return "00:00:00.000";
  const totalMs = Math.round(seconds * 1000);
  const h = Math.floor(totalMs / 3600000);
  const m = Math.floor((totalMs % 3600000) / 60000);
  const s = Math.floor((totalMs % 60000) / 1000);
  const ms = totalMs % 1000;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
}

export function formatDuration(
  duration: number | string | null | undefined,
): string {
  if (!duration && duration !== 0) {
    return "00:00";
  }

  const durationStr = String(duration);
  const sec = durationStr.includes(".")
    ? parseInt(durationStr.split(".")[0], 10)
    : Number(durationStr);

  if (isNaN(sec)) {
    return "00:00";
  }

  return formatClock(sec);
}

/**
 * Convert seconds to HH:MM:SS (always includes hours, zero-padded).
 *
 * @param secs - Total seconds
 * @returns Formatted string like "01:03:45"
 */
export function secondsToTime(secs: number): string {
  if (secs == null || isNaN(secs)) {
    return "00:00:00";
  }

  const hr = Math.floor(secs / 3600);
  const min = Math.floor((secs - hr * 3600) / 60);
  const sec = Math.floor(secs - hr * 3600 - min * 60);

  const pad2 = (n: number) => ("0" + n).slice(-2);

  return pad2(hr) + ":" + pad2(min) + ":" + pad2(sec);
}

/**
 * Convert seconds to a human-readable hour/minute/second string.
 *
 * @param secs - Total seconds
 * @returns Formatted string like "01 hrs 30 mins" or "05 mins 30 secs" or "45 secs"
 */
export function secondsToHour(secs: number): string {
  if (secs == null || isNaN(secs)) {
    return "00 secs";
  }

  const hr = Math.floor(secs / 3600);
  const min = Math.floor((secs - hr * 3600) / 60);
  const sec = Math.floor(secs - hr * 3600 - min * 60);

  const pad2 = (n: number) => ("0" + n).slice(-2);

  if (hr > 0) {
    return ("0" + hr).slice(-3) + " hrs " + pad2(min) + " mins";
  } else if (min > 0) {
    return pad2(min) + " mins " + pad2(sec) + " secs";
  } else {
    return pad2(secs) + " secs";
  }
}

/**
 * Format a date/string into a relative time string like "2 hours ago" or "In 3 days".
 *
 * @param value - Date object or ISO date string
 * @returns Relative time string
 */
export function formatTimeElapsed(
  value: Date | string | null | undefined,
): string {
  if (!value) {
    return "";
  }

  const d = value instanceof Date ? value : new Date(value);
  const now = new Date();
  const isFutureEvent = now < d;

  const seconds = Math.round(Math.abs((now.getTime() - d.getTime()) / 1000));
  const minutes = Math.round(Math.abs(seconds / 60));
  const hours = Math.round(Math.abs(minutes / 60));
  const days = Math.round(Math.abs(hours / 24));
  const months = Math.round(Math.abs(days / 30.416));
  const years = Math.round(Math.abs(days / 365));

  const prefix = isFutureEvent ? "In " : "";
  const suffix = isFutureEvent ? "" : " ago";

  if (seconds <= 45) {
    return prefix + "a few seconds" + suffix;
  } else if (seconds <= 90) {
    return prefix + "a minute" + suffix;
  } else if (minutes <= 45) {
    return prefix + minutes + " minutes" + suffix;
  } else if (minutes <= 90) {
    return prefix + "an hour" + suffix;
  } else if (hours <= 22) {
    return prefix + hours + " hours" + suffix;
  } else if (hours <= 36) {
    return prefix + "a day" + suffix;
  } else if (days <= 25) {
    return prefix + days + " days" + suffix;
  } else if (days <= 45) {
    return prefix + "a month" + suffix;
  } else if (days <= 345) {
    return prefix + months + " months" + suffix;
  } else if (days <= 545) {
    return prefix + "a year" + suffix;
  } else {
    return prefix + years + " years" + suffix;
  }
}

/**
 * Format a number with an ordinal suffix (1st, 2nd, 3rd, 4th, etc.).
 *
 * @param n - The number to format
 * @returns String with ordinal suffix
 */
export function formatOrdinal(n: number): string {
  if (n == null || isNaN(n)) {
    return "";
  }

  const abs = Math.abs(n);
  const lastTwo = abs % 100;
  const lastOne = abs % 10;

  let suffix: string;
  if (lastTwo >= 11 && lastTwo <= 13) {
    suffix = "th";
  } else if (lastOne === 1) {
    suffix = "st";
  } else if (lastOne === 2) {
    suffix = "nd";
  } else if (lastOne === 3) {
    suffix = "rd";
  } else {
    suffix = "th";
  }

  return n + suffix;
}

/**
 * Format a large number with K/M/B/T suffixes.
 *
 * @param input - The number to format
 * @param decimals - Number of decimal places (optional)
 * @returns Formatted string like "1.5K", "2.3M"
 */
export function formatNumberSuffix(input: number, decimals?: number): string {
  const suffixes = ["K", "M", "B", "T", "P", "E"];
  const isNegativeValue = input < 0;

  if (
    Number.isNaN(input) ||
    (input < 1000 && input >= 0) ||
    !isNumeric(input) ||
    (input < 0 && input > -1000)
  ) {
    if (decimals != null && isNumeric(input) && !(input < 0) && input !== 0) {
      return input.toFixed(decimals);
    } else {
      return String(input);
    }
  }

  const absInput = isNegativeValue ? input * -1 : input;
  const exp = Math.floor(Math.log(absInput) / Math.log(1000));
  const formatted = (absInput / Math.pow(1000, exp)).toFixed(decimals);

  return (isNegativeValue ? "-" : "") + formatted + suffixes[exp - 1];
}

/**
 * Format a count for display: full locale-grouped digits ("1,327"), falling back
 * to compact K/M/B notation only for very large values so a stat card stays
 * readable. Use this for dashboard metric values where "1K" would hide precision.
 */
export function formatCount(input: number): string {
  if (Number.isNaN(input) || !isNumeric(input)) return String(input);
  return Math.abs(input) < 100_000
    ? Math.round(input).toLocaleString("en-US")
    : formatNumberSuffix(input, 1);
}

function isNumeric(value: number): boolean {
  const abs = Math.abs(value);
  const str = String(abs);
  return /^-{0,1}\d+$/.test(str) || /^\d+\.\d+$/.test(str);
}

/**
 * Parse a time string (H:MM:SS.mmm, H:MM:SS, MM:SS) or a plain number into
 * a seconds value. Returns 0 when the input is null/undefined, and NaN when
 * the string cannot be parsed.
 */
export const parseTimeToSeconds = toSeconds;

/** Format aggregate seconds in words (e.g. "1,004 h 4 min"); empty for a missing, NaN or negative value. */
export function formatDurationHuman(
  seconds: number | null | undefined,
): string {
  if (seconds == null || isNaN(seconds) || seconds < 0) return "";
  return formatDurationWords(seconds);
}

/** Compact axis label for seconds in its largest whole unit: "30s", "40m", "16h", "1.5h". */
export function formatDurationShort(seconds: number): string {
  const value = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  const [amount, unit] = value >= 3600 ? [value / 3600, "h"] : value >= 60 ? [value / 60, "m"] : [value, "s"];
  return `${Number(amount.toFixed(1)).toLocaleString()}${unit}`;
}

/**
 * Compact millisecond duration for run timings: "340ms", "1.1s", "2m 14s".
 * Returns an em-dash placeholder when the duration is unknown.
 *
 * @param ms - Elapsed milliseconds
 * @returns Short human-readable duration
 */
export function formatDurationMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) {
    return "–";
  }
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  }
  const seconds = ms / 1000;
  if (seconds < 60) {
    return `${seconds.toFixed(seconds < 10 ? 1 : 0)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds - minutes * 60);
  if (minutes < 60) {
    return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes - hours * 60}m`;
}
